import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, Observable } from 'rxjs';
import { TemplateItemTemplate } from '../Model/Catalog/TemplateItemTemplate';
import { WawiList } from '../Model/wawi/WawiList';

@Injectable()
export class WawiListService {
    constructor(private http: HttpClient, public translate: TranslateService) {
    }

    getAllLists(customerId: string): Observable<WawiList[]> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };

        return this.http.get<WawiList[]>('api/WawiList/getAllLists', options);
    }

    getAllListsWithoutFields(customerId: string): Observable<WawiList[]> {
        const params = new HttpParams()
            .append('customerId', customerId);
        const options = { headers: new HttpHeaders().append('Content-Type', 'application/json'), params };
        return this.http.get<WawiList[]>('api/WawiList/getAllListsWithoutFields', options);
    }

    getAllMappingsWithoutFields(customerId: string): Observable<WawiList[]> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };

        return this.http.get<WawiList[]>('api/WawiList/getAllMappingsWithoutFields', options);
    }

    getAllMappings(customerId: string): Promise<WawiList[]> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };

        return lastValueFrom(this.http.get<WawiList[]>('api/WawiList/getAllMappings', options));
    }

    getAllListNames(customerId: string): Observable<string[]> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };

        return this.http.get<string[]>('api/WawiList/getAllListNames', options);
    }

    getList(listId: string, customerId: string): Observable<WawiList> {
        const params = new HttpParams()
            .append('listId', listId)
            .append('customerId', customerId);
        const options = { headers: new HttpHeaders().append('Content-Type', 'application/json'), params };

        return this.http.get<WawiList>('api/WawiList/getList', options);
    }

    getListByName(listName: string, customerId: string): Observable<WawiList> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('listName', listName).append('customerId', customerId)
        };

        return this.http.get<WawiList>('api/WawiList/getListByName', options);
    }

    getListValues(listId: string, customerId: string): Observable<string[]> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('listId', listId).append('customerId', customerId)
        };

        return this.http.get<string[]>('api/WawiList/getListValues', options);
    }

    getElementValue(listId: string, customerId: string, elementId: number): Observable<string> {
        const options: Object = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('listId', listId).append('customerId', customerId).append('elementId', elementId),
            responseType: 'text'
        };

        return this.http.get<string>('api/WawiList/GetElementValue', options);
    }

    create(entity: WawiList, customerId: string): Promise<WawiList> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };

        return lastValueFrom(this.http.post<WawiList>('api/WawiList/Create', entity, options));
    }

    update(entity: WawiList, customerId: string): Promise<WawiList> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };

        return lastValueFrom(this.http.post<WawiList>('api/WawiList/Update', entity, options));
    }

    updateOnlyList(entity: WawiList, customerId: string): Observable<WawiList> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };

        var result = this.http.post<WawiList>('api/WawiList/UpdateOnlyList', entity, options);
        return result;
    }

    delete(listId: string, customerId: string): Observable<object> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('listId', listId).append('customerId', customerId)
        };
        return this.http.get('api/WawiList/delete', options) as any;
    }

    startImport(customerId: string, filename: string): Observable<object> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId).append('fileName', filename)
        };
        return this.http.post('api/WawiList/StartImpotJob', undefined, options) as any;
    }

    startExport(customerId: string, listId: string): Observable<object> {
        const params = new HttpParams().append('listId', listId).append('customerId', customerId);
        const options = { headers: new HttpHeaders().append('Content-Type', 'application/json'), params };
        return this.http.post('api/WawiList/StartExportJob', undefined, options) as any;
    }

    getCatalogFieldValues(catalogId: string, customerId: string, fields: string[], limit?: number): Promise<string[]> {
        let params = new HttpParams()
            .append('catalogId', catalogId)
            .append('customerId', customerId)
            .append('fields', fields.join(','));
        if (limit != null) {
            params = params.append('limit', limit);
        }
        const options = { headers: new HttpHeaders().append('Content-Type', 'application/json'), params };
        return lastValueFrom(this.http.get<string[]>('api/WawiList/GetCatalogFieldValues', options));
    }

    getWawiMappingFields(customerId: string): Observable<TemplateItemTemplate[]> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };
        return this.http.get<TemplateItemTemplate[]>('api/WawiList/getWawiMappingFields', options);
    }
}
